import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Plug, Users, CheckCircle2, ArrowRight, ArrowLeft, Plus, X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STEPS = [
  { title: 'Organization Setup', icon: Building2 },
  { title: 'Connect Documents', icon: Plug },
  { title: 'Invite Team', icon: Users },
  { title: 'Ready!', icon: CheckCircle2 },
];

const FRAMEWORKS = ['HIPAA', 'CMS / Medicare', 'FDA', 'Joint Commission', 'SAMHSA', 'OIG', 'State Regulations'];
const DEPARTMENTS = ['Clinical', 'Compliance', 'Billing', 'IT Security', 'Quality', 'Legal', 'Operations', 'Behavioral Health'];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(['HIPAA', 'CMS / Medicare']);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(['Compliance', 'Clinical']);
  const [inviteEmails, setInviteEmails] = useState<{ email: string; role: string }[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('compliance_rep');

  const toggleItem = (item: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
  };

  const addInvite = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    setInviteEmails([...inviteEmails, { email: newEmail, role: newRole }]);
    setNewEmail('');
  };

  const handleComplete = async () => {
    try {
      // Send invitations
      for (const inv of inviteEmails) {
        try {
          await api.auth.invite({ email: inv.email, role: inv.role });
        } catch { /* non-fatal */ }
      }
      toast.success('Setup complete! Welcome to NOETED.');
      navigate('/');
    } catch {
      navigate('/');
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-background p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-teal">Welcome to NOETED</h1>
          <p className="mt-1 text-sm text-muted-foreground">Let's set up your compliance workspace</p>
        </div>

        {/* Step indicators */}
        <div className="mb-8 flex items-center justify-center gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex items-center gap-2">
                <div className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
                  i < step ? 'bg-teal text-white' : i === step ? 'bg-teal/20 text-teal ring-2 ring-teal' : 'bg-muted text-muted-foreground',
                )}>
                  {i < step ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                {i < STEPS.length - 1 && <div className={cn('h-0.5 w-8', i < step ? 'bg-teal' : 'bg-muted')} />}
              </div>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Step 0: Frameworks & Departments */}
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold">Regulatory Frameworks</h3>
                  <p className="mt-1 text-xs text-muted-foreground">Which frameworks does your organization need to track?</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {FRAMEWORKS.map((fw) => (
                      <button key={fw} type="button" onClick={() => toggleItem(fw, selectedFrameworks, setSelectedFrameworks)}
                        className={cn('rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          selectedFrameworks.includes(fw) ? 'border-teal bg-teal/10 text-teal-light' : 'border-border text-muted-foreground hover:border-teal/30')}>
                        {fw}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Departments</h3>
                  <p className="mt-1 text-xs text-muted-foreground">Select the departments that handle compliance tasks</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {DEPARTMENTS.map((dept) => (
                      <button key={dept} type="button" onClick={() => toggleItem(dept, selectedDepartments, setSelectedDepartments)}
                        className={cn('rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          selectedDepartments.includes(dept) ? 'border-teal bg-teal/10 text-teal-light' : 'border-border text-muted-foreground hover:border-teal/30')}>
                        {dept}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Connect documents */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Connect a Document Source</h3>
                <p className="text-xs text-muted-foreground">Connect your policy document repository for AI-powered analysis. You can always do this later from Settings.</p>
                <div className="grid grid-cols-3 gap-3">
                  {['SharePoint', 'Google Drive', 'SFTP'].map((type) => (
                    <button key={type} type="button" className="rounded-lg border border-border p-4 text-center text-sm hover:border-teal/50 hover:bg-teal/5">
                      <Plug className="mx-auto h-6 w-6 text-muted-foreground" />
                      <span className="mt-2 block text-xs font-medium">{type}</span>
                    </button>
                  ))}
                </div>
                <p className="text-center text-[10px] text-muted-foreground">You can configure connectors later from Admin → Connectors</p>
              </div>
            )}

            {/* Step 2: Invite team */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Invite Your Team</h3>
                <p className="text-xs text-muted-foreground">Add team members who will review compliance tasks</p>
                <div className="flex items-center gap-2">
                  <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="colleague@org.com" className="flex-1" />
                  <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="h-9 rounded border border-border bg-background px-2 text-xs">
                    <option value="compliance_rep">Compliance Rep</option>
                    <option value="department_rep">Department Rep</option>
                    <option value="cco">CCO</option>
                  </select>
                  <Button size="sm" variant="outline" onClick={addInvite}><Plus className="h-3.5 w-3.5" /></Button>
                </div>
                {inviteEmails.length > 0 && (
                  <div className="space-y-1">
                    {inviteEmails.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between rounded border border-border px-3 py-1.5 text-xs">
                        <span>{inv.email}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]">{inv.role.replace('_', ' ')}</Badge>
                          <button type="button" onClick={() => setInviteEmails(inviteEmails.filter((_, j) => j !== i))}><X className="h-3 w-3 text-muted-foreground" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-center text-[10px] text-muted-foreground">You can invite more team members later from Admin</p>
              </div>
            )}

            {/* Step 3: Done */}
            {step === 3 && (
              <div className="space-y-4 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-teal" />
                <h3 className="text-lg font-semibold">You're all set!</h3>
                <p className="text-sm text-muted-foreground">Your NOETED workspace is ready. Here's what's configured:</p>
                <div className="mx-auto max-w-xs space-y-2 text-left text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Frameworks:</span><span className="font-medium">{selectedFrameworks.length} selected</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Departments:</span><span className="font-medium">{selectedDepartments.length} active</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Team invites:</span><span className="font-medium">{inviteEmails.length} pending</span></div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between">
              {step > 0 ? (
                <Button variant="outline" size="sm" onClick={() => setStep(step - 1)}>
                  <ArrowLeft className="mr-1 h-3.5 w-3.5" />Back
                </Button>
              ) : <div />}

              {step < 3 ? (
                <div className="flex items-center gap-2">
                  {step > 0 && <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => setStep(step + 1)}>Skip</Button>}
                  <Button size="sm" className="bg-teal text-white hover:bg-teal/90" onClick={() => setStep(step + 1)}>
                    Next<ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button className="bg-teal text-white hover:bg-teal/90" onClick={handleComplete}>
                  Go to Dashboard<ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
